import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Pill, daysUntil, timeAgo, WeatherBadge } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Inbox, CalendarClock, Clock, AlertTriangle, ExternalLink, Bell, Sparkles,
  ListChecks, ArrowUpRight, GraduationCap, School, PlayCircle, ArrowRight, Info, X, Workflow,
} from "lucide-react";

export default function MissionControl() {
  const { user } = useAuth();
  const me = user?.email;
  const fullAccess = user?.department === "fulfillment" || user?.role === "admin";
  return (
    <div className="p-8 md:p-10 space-y-10">
      <PageHeader title="The Bulletin"
        subtitle={fullAccess
          ? "Your personal notice board over HubSpot + your AI automations. Everything that needs you, in one place."
          : "Welcome to Indie University! Pick up right where you left off in your learning below."} />
      <KatieWelcome email={me} />
      <YourLearning fullAccess={fullAccess} />
      {fullAccess && (
        <>
          <ForYou />
          <MyTasks />
          <FrontDesk me={me} />
          <DeadlineBoard me={me} />
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------ YOUR LEARNING (everyone) */
const COURSE_ICON = { orientation: School, trademark: GraduationCap };

/* One-time welcome for Katie Hooppaw pointing her to the SOP 2.0 lessons */
function KatieWelcome({ email }) {
  const navigate = useNavigate();
  const KEY = "katieh_sop_welcome_dismissed";
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(KEY) === "1");
  if ((email || "").toLowerCase() !== "katieh@indielaw.com" || dismissed) return null;
  const close = () => { localStorage.setItem(KEY, "1"); setDismissed(true); };
  return (
    <section data-testid="katieh-welcome" className="relative bg-[#141B4D] rounded-2xl p-6 md:p-7 overflow-hidden">
      <button onClick={close} data-testid="katieh-welcome-dismiss"
        className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors" aria-label="Dismiss">
        <X className="w-5 h-5" />
      </button>
      <div className="flex items-start gap-4">
        <span className="w-11 h-11 rounded-xl bg-[#FFD100] flex items-center justify-center shrink-0">
          <Workflow className="w-6 h-6 text-[#141B4D]" />
        </span>
        <div className="min-w-0">
          <h3 className="text-white font-bold text-lg">Welcome, Katie! 👋</h3>
          <p className="text-white/75 text-sm mt-1 leading-relaxed max-w-2xl">
            As our new Head of Client Operations, your best starting point is the <span className="text-[#FFD100] font-semibold">Fulfillment Operations — SOP 2.0</span> section
            at the top of The Trademark Classroom. It walks through exactly how the fulfillment team runs in HubSpot — tickets & tasks,
            front-of-house vs back-of-house, the onboarding and A–D trademark pipelines, and everything that changed in 2.0.
          </p>
          <Button onClick={() => navigate("/trademark-classroom")} data-testid="katieh-welcome-cta"
            className="mt-4 bg-[#FFD100] text-[#141B4D] hover:bg-[#ffdb33] font-bold">
            Open The Trademark Classroom <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </section>
  );
}

function YourLearning({ fullAccess }) {
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/learning/summary").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return null;
  return (
    <section data-testid="section-learning">
      <SectionHeader icon={GraduationCap} title="Your learning"
        subtitle="Start or continue your courses — jump straight to the next lesson." />
      <div className="grid md:grid-cols-2 gap-4">
        {d.courses.map((c) => {
          const Icon = COURSE_ICON[c.key] || GraduationCap;
          const pct = c.total ? Math.round((c.completed / c.total) * 100) : 0;
          const done = c.completed >= c.total && c.total > 0;
          return (
            <div key={c.key} data-testid={`learning-card-${c.key}`}
              className="bg-white rounded-xl p-5 shadow-sm border border-black/5 flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-9 h-9 rounded-lg bg-[#141B4D] flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#FFD100]" />
                </span>
                <div className="font-bold text-[#141B4D]">{c.title}</div>
                {done && <Pill tone="green" className="ml-auto">Complete</Pill>}
              </div>
              <div className="flex items-center gap-3 mb-1">
                <div className="flex-1 h-2 rounded-full bg-[#EDEAE1] overflow-hidden">
                  <div className="h-full bg-[#FFD100]" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-sm font-bold text-[#141B4D]">{pct}%</span>
              </div>
              <div className="text-xs text-[#101820]/50 mb-4">{c.completed} of {c.total} lessons complete</div>
              {!done && c.completed > 0 && c.next?.title && (
                <div className="text-xs text-[#101820]/70 mb-3 truncate">Up next: <span className="font-semibold text-[#141B4D]">{c.next.title}</span></div>
              )}
              <Button data-testid={`learning-continue-${c.key}`} onClick={() => navigate(c.route)}
                className="mt-auto h-10 bg-[#FFD100] text-[#101820] font-bold hover:brightness-95">
                {done ? (<>Review course <ArrowRight className="w-4 h-4 ml-2" /></>)
                  : c.completed === 0 ? (<><PlayCircle className="w-4 h-4 mr-2" /> Start course</>)
                  : (<>Continue course <ArrowRight className="w-4 h-4 ml-2" /></>)}
              </Button>
            </div>
          );
        })}
      </div>
      {fullAccess && (
        <div data-testid="learning-explainer" className="mt-4 flex items-start gap-2.5 bg-[#EAF1F9] border border-[#BDC5DB]/60 rounded-xl p-4">
          <Info className="w-4 h-4 text-[#1D57A5] mt-0.5 shrink-0" />
          <p className="text-sm text-[#101820]/75 leading-relaxed">
            <span className="font-semibold text-[#141B4D]">Two courses, two purposes:</span> <span className="font-semibold">New Teammate Orientation</span> is the company-wide welcome every teammate gets. <span className="font-semibold">The Trademark Classroom</span> is your in-depth fulfillment training — SOPs, office actions, searches, and everything you need to do the work.
          </p>
        </div>
      )}
    </section>
  );
}

function SectionHeader({ icon: Icon, title, subtitle, right }) {
  return (
    <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
      <div>
        <div className="flex items-center gap-2 text-[#141B4D]">
          <Icon className="w-5 h-5 text-[#1D57A5]" />
          <h2 className="text-lg font-bold">{title}</h2>
        </div>
        {subtitle && <p className="text-sm text-[#101820]/55 mt-0.5">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

function ScopeToggle({ scope, setScope, testid }) {
  return (
    <div className="inline-flex rounded-lg border border-[#BDC5DB] overflow-hidden bg-white text-sm font-semibold">
      <button data-testid={`${testid}-mine`} onClick={() => setScope("mine")}
        className={`px-3 py-1.5 ${scope === "mine" ? "bg-[#141B4D] text-white" : "text-[#141B4D]"}`}>Just mine</button>
      <button data-testid={`${testid}-firm`} onClick={() => setScope("firm")}
        className={`px-3 py-1.5 ${scope === "firm" ? "bg-[#141B4D] text-white" : "text-[#141B4D]"}`}>Whole firm</button>
    </div>
  );
}

/* ------------------------------------------------ FOR YOU (hero) */
const SOURCE_TONE = { HubSpot: "yellow", Slack: "cobalt", "Almira's Desk": "green", "Review Pipeline": "navy" };

function ForYou() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/for-you").then(({ data }) => setItems(data)); }, []);
  return (
    <section data-testid="section-foryou" className="rounded-2xl bg-[#141B4D] p-6 md:p-7 shadow-lg">
      <div className="flex items-center gap-2 mb-1">
        <Bell className="w-5 h-5 text-[#FFD100]" />
        <h2 className="text-xl font-bold text-white">For You — what needs you today</h2>
      </div>
      <p className="text-sm text-white/60 mb-5">Aggregated from HubSpot & Slack and prioritized for you. This is the one list to work top-to-bottom.</p>
      {items.length === 0 && <div className="text-white/70 p-6 bg-white/5 rounded-xl">You're all caught up. 🎉</div>}
      <div className="grid md:grid-cols-2 gap-3">
        {items.map((n) => (
          <a key={n.id} href={n.link} data-testid={`foryou-item-${n.id}`}
            className="block bg-white rounded-xl p-4 shadow-sm border border-black/5 card-lift">
            <div className="flex items-center gap-2 mb-1.5">
              <Pill tone={SOURCE_TONE[n.source] || "pale"}>{n.source}</Pill>
              <span className="text-xs text-[#101820]/45">{n.kind}</span>
              <span className="ml-auto text-xs text-[#101820]/40">{timeAgo(n.created)}</span>
              {n.priority === 1 && <span className="w-2 h-2 rounded-full bg-[#B91C1C]" title="High priority" />}
            </div>
            <div className="text-[15px] text-[#141B4D] font-medium">{n.title}</div>
          </a>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------ MY TASKS (AI summary) */
function MyTasks() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/mission/overview").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return null;
  const stages = Object.entries(d.pipeline_counts || {});
  const TaskRow = ({ t, overdue }) => (
    <div data-testid={`task-${overdue ? "overdue" : "upcoming"}-${t.matter_id}`}
      className={`flex items-center justify-between gap-3 py-2.5 px-3 rounded-lg ${overdue ? "bg-[#FBE9E9]" : "bg-[#FAF7F0]"}`}>
      <div className="min-w-0">
        <div className="font-semibold text-[#141B4D] truncate">{t.mark} <span className="font-normal text-[#101820]/50">· {t.type}</span></div>
        <div className="text-xs text-[#101820]/50">{t.company}{t.serial_number ? ` · ${t.serial_number}` : ""}</div>
      </div>
      <div className={`text-sm font-bold shrink-0 ${overdue ? "text-[#B91C1C]" : t.days < 14 ? "text-[#B45309]" : "text-[#101820]/60"}`}>
        {t.due} <span className="font-normal">({t.days}d)</span>
      </div>
    </div>
  );
  return (
    <section data-testid="section-mytasks">
      <SectionHeader icon={ListChecks} title="My Tasks" subtitle="An AI summary of your upcoming & overdue HubSpot tasks."
        right={<a data-testid="hubspot-link" href={d.hubspot_url} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-sm font-semibold bg-[#FFD100] text-[#101820] rounded-lg px-3.5 py-2 hover:brightness-95">
          View client matters in HubSpot <ArrowUpRight className="w-4 h-4" /></a>} />

      <div className="bg-white rounded-xl p-5 shadow-sm border border-black/5">
        <div className="flex items-start gap-2.5">
          <Sparkles className="w-4 h-4 text-[#1D57A5] mt-1 shrink-0" />
          <p data-testid="mytasks-summary" className="text-[15px] text-[#101820] leading-relaxed">{d.summary}</p>
        </div>
        <div className="flex gap-2 mt-4 flex-wrap">
          <Pill tone={d.overdue.length ? "red" : "green"}>{d.overdue.length} overdue</Pill>
          <Pill tone="cobalt">{d.upcoming.length} upcoming (90d)</Pill>
          <Pill tone="pale">{d.total_matters} matters you own</Pill>
        </div>

        {stages.length > 0 && (
          <div className="mt-4 pt-4 border-t border-[#EDEAE1]">
            <div className="text-xs font-bold uppercase tracking-widest text-[#101820]/45 mb-2">My pipeline at a glance</div>
            <div className="flex flex-wrap gap-2" data-testid="pipeline-glance">
              {stages.map(([stage, count]) => (
                <div key={stage} className="flex items-center gap-2 bg-[#F2E9DB] rounded-lg px-3 py-1.5 text-sm">
                  <span className="text-[#141B4D]">{stage}</span>
                  <span className="font-bold text-[#141B4D] bg-white rounded-full px-1.5">{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {(d.overdue.length > 0 || d.upcoming.length > 0) && (
          <div className="mt-4 pt-4 border-t border-[#EDEAE1] space-y-1.5">
            {d.overdue.map((t) => <TaskRow key={t.matter_id + t.type} t={t} overdue />)}
            {d.upcoming.map((t) => <TaskRow key={t.matter_id + t.type} t={t} />)}
          </div>
        )}
      </div>
    </section>
  );
}

/* ------------------------------------------------ FRONT DESK */
function FrontDesk({ me }) {
  const [items, setItems] = useState([]);
  const [scope, setScope] = useState("mine");
  const load = async () => setItems((await api.get("/front-desk")).data);
  useEffect(() => { load(); }, []);
  const ack = async (id) => { await api.post(`/front-desk/${id}/ack`); toast.success("Acknowledged"); load(); };
  const shown = scope === "mine" && me ? items.filter((it) => it.owner === me) : items;

  return (
    <section data-testid="section-frontdesk">
      <SectionHeader icon={Inbox} title="Your Front Desk"
        subtitle="Client threads that reference you — Viktor's hello@ monitor, HubSpot SLA timers & Slack. Anything past 24h shows loud."
        right={<ScopeToggle scope={scope} setScope={setScope} testid="frontdesk-scope" />} />
      <div className="space-y-3">
        {shown.length === 0 && <div className="text-[#101820]/50 p-5 bg-white rounded-xl border border-black/5">Nothing waiting on you here. 🎉</div>}
        {shown.map((it) => {
          const overdue = it.response_clock_hours >= 24;
          return (
            <div key={it.id} data-testid={`frontdesk-item-${it.id}`}
              className={`bg-white rounded-xl p-4 shadow-sm border-l-4 flex items-start justify-between gap-4 card-lift ${overdue ? "border-[#B91C1C]" : "border-[#1D57A5]"}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Pill tone={overdue ? "red" : "cobalt"}>{it.type}</Pill>
                  <Pill tone="pale">{it.source}</Pill>
                  {overdue && <span className="flex items-center gap-1 text-xs font-bold text-[#B91C1C]"><AlertTriangle className="w-3.5 h-3.5" /> {it.response_clock_hours}h waiting</span>}
                </div>
                <div className="font-semibold text-[#141B4D] mt-2">{it.subject}</div>
                <div className="text-sm text-[#101820]/60 mt-0.5">"{it.snippet}"</div>
                <div className="text-xs text-[#101820]/45 mt-2">{it.contact?.name} · {it.contact?.company} · owner {(it.owner || "unassigned").split("@")[0]}</div>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <div className={`flex items-center gap-1 text-sm font-bold ${overdue ? "text-[#B91C1C]" : "text-[#1D57A5]"}`}>
                  <Clock className="w-4 h-4" /> {it.response_clock_hours}h
                </div>
                <a href={it.thread_link} target="_blank" rel="noreferrer" className="text-xs text-[#1D57A5] flex items-center gap-1 hover:underline">
                  Open thread <ExternalLink className="w-3 h-3" />
                </a>
                {!it.acknowledged
                  ? <Button data-testid={`ack-${it.id}`} onClick={() => ack(it.id)} className="h-8 bg-[#FFD100] text-[#101820] font-bold hover:brightness-95 text-xs">Acknowledge</Button>
                  : <Pill tone="green">Auto-acknowledged</Pill>}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ------------------------------------------------ DEADLINE BOARD */
const BUCKETS = ["Action Required", "Maintenance & Renewals", "Tracking Only"];
const BUCKET_TONE = { "Action Required": "red", "Maintenance & Renewals": "green", "Tracking Only": "cobalt" };

function DeadlineBoard({ me }) {
  const [rows, setRows] = useState([]);
  const [bucket, setBucket] = useState("all");
  const [scope, setScope] = useState("mine");
  useEffect(() => { api.get("/deadlines").then(({ data }) => setRows(data)); }, []);
  let shown = scope === "mine" && me ? rows.filter((r) => r.owner === me) : rows;
  shown = bucket === "all" ? shown : shown.filter((r) => r.bucket === bucket);

  return (
    <section data-testid="section-deadlines">
      <SectionHeader icon={CalendarClock} title="Your Deadlines" subtitle="Deadlines on the matters you own. Toggle to the firm-wide view if you cover others."
        right={<ScopeToggle scope={scope} setScope={setScope} testid="deadline-scope" />} />
      <div className="flex gap-2 mb-4 flex-wrap">
        <button onClick={() => setBucket("all")} data-testid="deadline-bucket-all"
          className={`px-3 py-1.5 rounded-full text-sm font-semibold ${bucket === "all" ? "bg-[#141B4D] text-white" : "bg-white text-[#141B4D] border border-[#BDC5DB]"}`}>All</button>
        {BUCKETS.map((b) => (
          <button key={b} onClick={() => setBucket(b)} data-testid={`deadline-bucket-${b}`}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold ${bucket === b ? "bg-[#141B4D] text-white" : "bg-white text-[#141B4D] border border-[#BDC5DB]"}`}>{b}</button>
        ))}
      </div>
      {shown.length === 0 ? (
        <div className="text-[#101820]/50 p-5 bg-white rounded-xl border border-black/5">No deadlines in this view.</div>
      ) : (
        <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-black/5">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#141B4D] text-[#F2E9DB] text-left">
                <th className="p-4 font-medium">Mark</th><th className="p-4 font-medium">Serial</th>
                <th className="p-4 font-medium">Deadline</th><th className="p-4 font-medium">Risk</th><th className="p-4 font-medium">Bucket</th>
                <th className="p-4 font-medium">Owner</th><th className="p-4 font-medium">Due</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r, i) => {
                const dd = daysUntil(r.due);
                return (
                  <tr key={i} data-testid={`deadline-row-${i}`} className="even:bg-[#F2E9DB] odd:bg-white border-t border-[#BDC5DB]/30">
                    <td className="p-4 font-semibold text-[#141B4D]">{r.mark}</td>
                    <td className="p-4 font-mono text-xs">{r.serial_number || "—"}</td>
                    <td className="p-4">{r.type}</td>
                    <td className="p-4">{r.weather ? <WeatherBadge value={r.weather} /> : <span className="text-[#101820]/30">—</span>}</td>
                    <td className="p-4"><Pill tone={BUCKET_TONE[r.bucket]}>{r.bucket}</Pill></td>
                    <td className="p-4">{(r.owner || "—").split("@")[0]}</td>
                    <td className="p-4"><span className={dd < 14 ? "text-[#B91C1C] font-semibold" : ""}>{r.due} ({dd}d)</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
